import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactiveJsonRoot } from '@ea-lab/reactive-json';
import { Pencil, Trash2, Plus, Check, X, Search } from 'lucide-react';
import { DRUPAL_BASE_URL } from '../../config/drupal.js';
import { ensureCsrfToken } from '../../services/csrf.js';
import ConfirmTrashModal from '../shared/ui/ConfirmTrashModal.jsx';
import { getTagColorClass, getTagRemoveBtnClass } from '../../utils/tagColors.js';
import Toggle from '../shared/ui/Toggle.jsx';

const API_BASE = `${DRUPAL_BASE_URL}/decoupled-config-api`;

// Les CATÉGORIES sont du contenu : des termes du vocabulaire `eca_category`,
// servis par l'API decoupled générique en « Syncable Objects » — chaque terme
// transporte ses propres `submission_url` / `delete_url`, jamais reconstruites
// ici (l'absence d'URL EST le contrôle d'accès).
const CAT_COLLECTION = `${DRUPAL_BASE_URL}/decoupled-api/taxonomy_term/eca_category/collection?display=decoupled`;
const CAT_CREATE     = `${DRUPAL_BASE_URL}/decoupled-api/save?display=decoupled&entity_type=taxonomy_term&bundle=eca_category`;

// Rubrique active de la colonne de configuration. Constante de MODULE : la
// donnée d'un `ReactiveJsonRoot` est calée sur l'IDENTITÉ de `dataOverride`,
// donc un objet recréé à chaque rendu relancerait le fetch du menu (flash).
const SETTINGS_MENU_DATA = { activeItem: 'eca' };

// Valeurs de filtre qui ne sont pas une catégorie.
const FILTER_ALL  = 'all';
const FILTER_NONE = 'none';

// Second axe de filtre : l'état du modèle (`status` du config entity ECA).
const STATUS_ALL      = 'all';
const STATUS_ENABLED  = 'enabled';
const STATUS_DISABLED = 'disabled';

/** Un modèle ECA est actif sauf si Drupal a explicitement mis `status: false`. */
const isEnabled = (model) => model.status !== false;

/**
 * Ids de catégories STOCKÉS sur un modèle, sans filtrage : base des écritures.
 * Tolère l'ancien format mono-valué `category` tant qu'un modèle n'a pas été
 * réenregistré.
 */
const storedCategoryIds = (model) => {
  const editor = model.third_party_settings?.eca_react_editor ?? {};
  if (Array.isArray(editor.categories)) return editor.categories.map(String);
  const legacy = editor.category;
  return legacy === undefined || legacy === null || legacy === ''
    ? []
    : [String(legacy)];
};

/**
 * Champ « free tagging » des catégories d'un modèle — même geste que les tags
 * du kanban : des pastilles retirables, et un « + » qui ouvre un champ à
 * autocomplétion. Une saisie qui ne correspond à aucune catégorie en CRÉE une
 * (c'est ça, le free tagging) ; d'où le multi-valué.
 *
 * L'autocomplétion est un `datalist` natif : la liste de suggestions du kanban
 * est un `div` cliquable, hors d'atteinte au clavier — on garde le geste, pas
 * le défaut.
 */
const CategoryTagsField = ({ modelId, tags, options, labelledBy, onAdd, onRemove }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [input, setInput] = useState('');
  const domId   = String(modelId).replace(/[^A-Za-z0-9_-]/g, '-');
  const inputId = `eca-cat-add-${domId}`;
  const listId  = `eca-cat-options-${domId}`;

  const commit = () => {
    const value = input.trim();
    setInput('');
    setIsAdding(false);
    if (value) onAdd(value);
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-labelledby={labelledBy}>
      {tags.map(tag => (
        <span
          key={tag.value}
          className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] font-medium ${getTagColorClass(tag.label)}`}
        >
          {tag.label}
          <button
            type="button"
            className={`rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-500 ${getTagRemoveBtnClass(tag.label)}`}
            aria-label={`Retirer la catégorie ${tag.label}`}
            onClick={() => onRemove(tag.value)}
          >
            <X size={11} aria-hidden="true" />
          </button>
        </span>
      ))}

      {isAdding ? (
        <>
          <label className="sr-only" htmlFor={inputId}>
            Ajouter une catégorie (une catégorie inconnue est créée)
          </label>
          <input
            id={inputId}
            list={listId}
            autoFocus
            className="input-inline w-40 px-2 py-0.5 text-[11px]"
            placeholder="Catégorie…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onBlur={commit}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); commit(); }
              if (e.key === 'Escape') { setInput(''); setIsAdding(false); }
            }}
          />
          <datalist id={listId}>
            {options
              .filter(o => !tags.some(t => t.value === o.value))
              .map(o => <option key={o.value} value={o.label} />)}
          </datalist>
        </>
      ) : (
        <button
          type="button"
          className="rounded border border-dashed border-slate-300 px-2 py-0.5 text-[11px] text-slate-500 transition-colors hover:border-iris-300 hover:text-iris-600"
          onClick={() => { setInput(''); setIsAdding(true); }}
        >
          <Plus size={11} className="inline align-[-1px]" aria-hidden="true" />
          <span className="ml-1">Catégorie</span>
        </button>
      )}
    </div>
  );
};

/**
 * Lists all ECA models available in Drupal and lets the user open one in the editor.
 *
 * Deuxième colonne : les catégories de processus. Elle sert à la fois de
 * FILTRE de la liste et d'écran de gestion (ajout / renommage / suppression)
 * des catégories.
 *
 * Où vit quoi :
 *   - la catégorie elle-même  = un terme `eca_category` (CONTENU, CRUD via
 *     /decoupled-api/taxonomy_term/eca_category — cf. eca_react_editor.install) ;
 *   - le lien modèle→catégories = `third_party_settings.eca_react_editor.categories`
 *     du modèle ECA (un TABLEAU d'ID de termes, en chaînes), écrit via
 *     /decoupled-config-api. L'ancien format mono-valué `…eca_react_editor.category`
 *     reste LU (modèles déjà enregistrés) et disparaît à la première écriture.
 *
 * Config-first : cet écran reste du React parce que la liste ECA lit
 * /decoupled-config-api/eca, qui ne renvoie PAS de Syncable Object (ni
 * `submission_url` ni `delete_url` à suivre), et parce qu'affecter une
 * catégorie impose de FUSIONNER `third_party_settings.eca_react_editor` — sans
 * quoi les positions de nœuds sauvegardées par l'éditeur seraient écrasées.
 * Aucun DataSync ne peut l'exprimer sans fabriquer d'URL ; d'où l'extension du
 * composant existant, sans nouveau fichier.
 */
export const EcaModelList = ({ plugins }) => {
  const navigate = useNavigate();
  const [models, setModels]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // ── Catégories (termes eca_category) ─────────────────────────────────
  const [categories, setCategories] = useState([]);
  const [catError, setCatError]     = useState(null);
  const [catBusy, setCatBusy]       = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [editingId, setEditingId]   = useState(null);
  const [editingName, setEditingName] = useState('');
  const [catToDelete, setCatToDelete] = useState(null);

  // ── Filtres de la liste ──────────────────────────────────────────────
  const [activeFilter, setActiveFilter] = useState(FILTER_ALL);
  const [statusFilter, setStatusFilter] = useState(STATUS_ALL);
  const [query, setQuery] = useState('');

  // Erreur d'écriture SUR UN MODÈLE (catégorie, activation) : elle n'a rien à
  // faire dans la colonne des catégories, qui parle du vocabulaire.
  const [modelError, setModelError] = useState(null);

  const loadCategories = useCallback(() => {
    return fetch(CAT_COLLECTION, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          setCategories(data);
          setCatError(null);
        } else {
          setCatError(data?.status?.message ?? 'Format de réponse inattendu');
        }
      })
      .catch(e => setCatError(e.message));
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/eca`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          setModels(data);
        } else {
          setError(data?.status?.message ?? 'Unexpected response format');
        }
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const getLabel = (m) =>
    m.third_party_settings?.modeler_api?.label
    || m.label
    || m.id;

  // Catégories triées par intitulé — l'ordre d'un vocabulaire à plat n'a pas
  // de sens ici, et le tri alphabétique rend la colonne prévisible.
  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) =>
      String(a.data?.name ?? '').localeCompare(String(b.data?.name ?? ''), 'fr')),
    [categories],
  );

  const knownCatIds = useMemo(
    () => new Set(categories.map(c => String(c.data?.id))),
    [categories],
  );

  /** Catégories affichables d'un modèle : ids connus, dédoublonnés. */
  const categoriesOf = useCallback((m) => {
    const out = [];
    for (const id of storedCategoryIds(m)) {
      if (knownCatIds.has(id) && !out.includes(id)) out.push(id);
    }
    return out;
  }, [knownCatIds]);

  /** Options proposées par le champ free tagging. */
  const catOptions = useMemo(
    () => sortedCategories.map(cat => ({
      value: String(cat.data?.id),
      label: String(cat.data?.name ?? cat.data?.id),
    })),
    [sortedCategories],
  );

  // Un modèle compte dans CHACUNE de ses catégories ; « sans catégorie » = aucune.
  const countsByCategory = useMemo(() => {
    const counts = { [FILTER_NONE]: 0 };
    for (const m of models) {
      const ids = categoriesOf(m);
      if (!ids.length) {
        counts[FILTER_NONE] += 1;
        continue;
      }
      for (const id of ids) counts[id] = (counts[id] ?? 0) + 1;
    }
    return counts;
  }, [models, categoriesOf]);

  const countsByStatus = useMemo(() => {
    const enabled = models.filter(isEnabled).length;
    return {
      [STATUS_ALL]: models.length,
      [STATUS_ENABLED]: enabled,
      [STATUS_DISABLED]: models.length - enabled,
    };
  }, [models]);

  // Les trois axes (catégorie, état, recherche) se cumulent.
  const visibleModels = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return models.filter(m => {
      const ids = categoriesOf(m);
      if (activeFilter === FILTER_NONE && ids.length) return false;
      if (activeFilter !== FILTER_ALL && activeFilter !== FILTER_NONE
          && !ids.includes(activeFilter)) return false;
      if (statusFilter === STATUS_ENABLED && !isEnabled(m)) return false;
      if (statusFilter === STATUS_DISABLED && isEnabled(m)) return false;
      if (!needle) return true;
      return `${getLabel(m)} ${m.id}`.toLowerCase().includes(needle);
    });
  }, [models, activeFilter, statusFilter, query, categoriesOf]);

  // ── Écritures : catégories (contenu) ─────────────────────────────────

  /** POST sur une URL portée par la donnée (création, renommage, suppression). */
  const postSyncable = async (url, body) => {
    const resp = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const result = await resp.json().catch(() => ({}));
    if (!resp.ok || result?.status?.type === 'error') {
      throw new Error(result?.status?.message ?? `HTTP ${resp.status}`);
    }
    return result;
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    const name = newCatName.trim();
    if (!name || catBusy) return;
    setCatBusy(true);
    try {
      // La réponse de création ne reporte pas `delete_url` : on recharge la
      // collection, seule à servir des Syncable Objects complets.
      await postSyncable(CAT_CREATE, { data: { name, description: '' } });
      setNewCatName('');
      await loadCategories();
      setCatError(null);
    } catch (err) {
      setCatError(err.message);
    } finally {
      setCatBusy(false);
    }
  };

  const handleRenameCategory = async (cat) => {
    const name = editingName.trim();
    setEditingId(null);
    if (!name || name === cat.data?.name) return;
    setCatBusy(true);
    try {
      await postSyncable(cat.submission_url, { data: { ...cat.data, name } });
      setCategories(prev => prev.map(c =>
        c.data?.id === cat.data?.id ? { ...c, data: { ...c.data, name } } : c));
      setCatError(null);
    } catch (err) {
      setCatError(err.message);
    } finally {
      setCatBusy(false);
    }
  };

  const handleDeleteCategory = async () => {
    const cat = catToDelete;
    if (!cat) return;
    setCatBusy(true);
    try {
      await postSyncable(cat.delete_url, {});
      setCatToDelete(null);
      if (activeFilter === String(cat.data?.id)) setActiveFilter(FILTER_ALL);
      await loadCategories();
      setCatError(null);
    } catch (err) {
      setCatError(err.message);
      setCatToDelete(null);
    } finally {
      setCatBusy(false);
    }
  };

  // ── Écritures sur un modèle (config) ─────────────────────────────────

  /**
   * PATCH partiel sur un modèle ECA : le back n'applique que les clés reçues.
   * Mise à jour optimiste, repli sur l'état précédent si l'écriture échoue.
   */
  const patchModel = async (model, patch, errorPrefix) => {
    const previous = models;
    setModels(prev => prev.map(m => (m.id === model.id ? { ...m, ...patch } : m)));

    try {
      // /decoupled-config-api n'est pas couvert par le patch CSRF global
      // (DRUPAL_API_PATTERN ne connaît que /decoupled-api/) : token explicite.
      const token = await ensureCsrfToken();
      const resp = await fetch(
        `${API_BASE}/save?entity_type=eca&id=${encodeURIComponent(model.id)}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'X-CSRF-Token': token } : {}),
          },
          body: JSON.stringify({ data: patch }),
        },
      );
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || result?.status?.type === 'error') {
        throw new Error(result?.status?.message ?? `HTTP ${resp.status}`);
      }
      setModelError(null);
    } catch (err) {
      setModels(previous);
      setModelError(`${errorPrefix} : ${err.message}`);
    }
  };

  /**
   * Écrit `third_party_settings.eca_react_editor.categories` sur le modèle ECA.
   * La FUSION est indispensable : `set('third_party_settings', …)` remplace la
   * clé entière côté Drupal, donc envoyer les seules catégories effacerait les
   * positions de nœuds enregistrées par l'éditeur.
   */
  const handleSetCategories = async (model, nextIds) => {
    const tps = { ...(model.third_party_settings ?? {}) };
    const editor = { ...(tps.eca_react_editor ?? {}) };
    // Le mono-valué historique ne survit pas à une écriture : `categories` fait foi.
    delete editor.category;
    if (nextIds.length) {
      editor.categories = nextIds;
    } else {
      delete editor.categories;
    }
    if (Object.keys(editor).length) {
      tps.eca_react_editor = editor;
    } else {
      delete tps.eca_react_editor;
    }

    await patchModel(model, { third_party_settings: tps }, 'Catégorie non enregistrée');
  };

  /**
   * Active / désactive un modèle — le `status` du config entity ECA, celui-là
   * même que lit le moteur : un modèle désactivé ne s'exécute plus.
   */
  const handleToggleStatus = (model) =>
    patchModel(
      model,
      { status: !isEnabled(model) },
      isEnabled(model) ? 'Désactivation non enregistrée' : 'Activation non enregistrée',
    );

  /**
   * Free tagging : ajoute une catégorie à un modèle à partir d'un LIBELLÉ.
   * Libellé déjà connu → on réutilise le terme ; sinon on le crée, puis on
   * l'affecte. La comparaison ignore la casse pour ne pas semer de doublons
   * « Ventes » / « ventes ».
   */
  const handleAddCategory = async (model, rawName) => {
    const name = rawName.trim();
    if (!name || catBusy) return;

    const match = categories.find(c =>
      String(c.data?.name ?? '').trim().toLowerCase() === name.toLowerCase());
    let id = match ? String(match.data?.id) : '';

    if (!id) {
      setCatBusy(true);
      try {
        // La réponse de création porte l'`id` du terme, mais pas ses URLs de
        // Syncable Object : on recharge la collection pour la colonne de gauche.
        const created = await postSyncable(CAT_CREATE, { data: { name, description: '' } });
        id = created?.id === undefined || created?.id === null ? '' : String(created.id);
        await loadCategories();
        setCatError(null);
      } catch (err) {
        setCatError(err.message);
        return;
      } finally {
        setCatBusy(false);
      }
    }

    const current = storedCategoryIds(model);
    if (!id || current.includes(id)) return;
    await handleSetCategories(model, [...current, id]);
  };

  const handleRemoveCategory = (model, catId) =>
    handleSetCategories(model, storedCategoryIds(model).filter(v => v !== String(catId)));

  // ── Rendu ────────────────────────────────────────────────────────────

  /** Identifiant de modèle réduit à ce qu'un id DOM accepte. */
  const domIdOf = (m) => String(m.id).replace(/[^A-Za-z0-9_-]/g, '-');

  const filterRowClass = (isActive) =>
    'flex-1 min-w-0 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-left transition-colors '
    + (isActive
      ? 'bg-iris-50 text-iris-700'
      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900');

  const deletedCatCount = catToDelete
    ? (countsByCategory[String(catToDelete.data?.id)] ?? 0)
    : 0;

  return (
    <div className="bg-slate-50 flex flex-col min-h-[100dvh] h-[100dvh] max-h-[100dvh] overflow-hidden font-sans">
      <div className="shrink-0">
        <ReactiveJsonRoot
          rjBuildUrl="/components/layout/Navigation.yaml"
          rjBuildFetchMethod="GET"
          plugins={plugins}
        />
      </div>

      {/* Colonne de configuration + contenu — même gabarit que /configuration
          et que les réglages CRM. La colonne EST le YAML SettingsMenu.yaml,
          monté en root : aucun exemplaire du menu à maintenir ici. */}
      <div className="flex min-h-0 flex-1 w-full overflow-hidden">
        <ReactiveJsonRoot
          rjBuildUrl="/components/layout/SettingsMenu.yaml"
          rjBuildFetchMethod="GET"
          dataOverride={SETTINGS_MENU_DATA}
          plugins={plugins}
        />

        {/* ── Colonne 2 : les catégories (filtre + gestion) ───────────── */}
        <aside
          className="flex w-64 min-h-0 flex-shrink-0 flex-col border-r border-gray-200 bg-white"
          aria-labelledby="eca-filters-title"
        >
          <div className="flex-shrink-0 border-b border-gray-200 px-5 py-4">
            <h2 id="eca-filters-title" className="text-sm font-bold text-gray-900">Filtres</h2>
            <p className="text-xs text-gray-500 mt-1">
              Organisez les modèles par catégorie, et filtrez-les par état.
            </p>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            {catError && (
              <div className="alert alert-danger text-xs mb-2" role="alert">
                {catError}
              </div>
            )}

            <h3 id="eca-cat-filter" className="field-label text-xs mb-1 px-3">Catégorie</h3>
            <ul className="space-y-1" aria-labelledby="eca-cat-filter">
              <li>
                <button
                  type="button"
                  className={filterRowClass(activeFilter === FILTER_ALL)}
                  aria-current={activeFilter === FILTER_ALL ? 'true' : undefined}
                  onClick={() => setActiveFilter(FILTER_ALL)}
                >
                  <span className="truncate">Tous les modèles</span>
                  <span className="badge badge-neutral badge-sm ml-auto shrink-0">
                    {models.length}
                  </span>
                </button>
              </li>

              {sortedCategories.map(cat => {
                const id = String(cat.data?.id);
                const name = cat.data?.name ?? id;
                const isActive = activeFilter === id;
                const isEditing = editingId === id;

                return (
                  <li key={id} className="group flex items-center gap-0.5">
                    {isEditing ? (
                      <>
                        <label className="sr-only" htmlFor={`eca-cat-rename-${id}`}>
                          Nouvel intitulé de la catégorie {name}
                        </label>
                        <input
                          id={`eca-cat-rename-${id}`}
                          className="input-inline flex-1 min-w-0 px-3 py-2 text-sm"
                          value={editingName}
                          autoFocus
                          onChange={e => setEditingName(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleRenameCategory(cat);
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                        />
                        <button
                          type="button"
                          className="p-1.5 rounded text-slate-400 hover:text-emerald-600 shrink-0"
                          aria-label={`Valider le renommage de ${name}`}
                          onClick={() => handleRenameCategory(cat)}
                        >
                          <Check size={14} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="p-1.5 rounded text-slate-400 hover:text-slate-700 shrink-0"
                          aria-label={`Annuler le renommage de ${name}`}
                          onClick={() => setEditingId(null)}
                        >
                          <X size={14} aria-hidden="true" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className={filterRowClass(isActive)}
                          aria-current={isActive ? 'true' : undefined}
                          onClick={() => setActiveFilter(id)}
                        >
                          <span className="truncate">{name}</span>
                          <span className="badge badge-neutral badge-sm ml-auto shrink-0">
                            {countsByCategory[id] ?? 0}
                          </span>
                        </button>

                        {/* Le back n'émet ces URLs que pour qui a le droit :
                            leur absence EST le contrôle d'accès. */}
                        {cat.submission_url && (
                          <button
                            type="button"
                            className="p-1.5 rounded text-slate-300 hover:text-iris-600 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                            aria-label={`Renommer la catégorie ${name}`}
                            onClick={() => { setEditingId(id); setEditingName(name); }}
                          >
                            <Pencil size={14} aria-hidden="true" />
                          </button>
                        )}
                        {cat.delete_url && (
                          <button
                            type="button"
                            className="p-1.5 rounded text-slate-300 hover:text-rose-600 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                            aria-label={`Supprimer la catégorie ${name}`}
                            onClick={() => setCatToDelete(cat)}
                          >
                            <Trash2 size={14} aria-hidden="true" />
                          </button>
                        )}
                      </>
                    )}
                  </li>
                );
              })}

              <li>
                <button
                  type="button"
                  className={filterRowClass(activeFilter === FILTER_NONE)}
                  aria-current={activeFilter === FILTER_NONE ? 'true' : undefined}
                  onClick={() => setActiveFilter(FILTER_NONE)}
                >
                  <span className="truncate text-slate-500">Sans catégorie</span>
                  <span className="badge badge-neutral badge-sm ml-auto shrink-0">
                    {countsByCategory[FILTER_NONE] ?? 0}
                  </span>
                </button>
              </li>
            </ul>

            {/* ── État — troisième axe, cumulatif avec catégorie et recherche ── */}
            <h3 id="eca-status-filter" className="field-label text-xs mt-5 mb-1 px-3">
              État
            </h3>
            <ul className="space-y-1" aria-labelledby="eca-status-filter">
              {[
                [STATUS_ALL,      'Tous les états'],
                [STATUS_ENABLED,  'Actifs'],
                [STATUS_DISABLED, 'Désactivés'],
              ].map(([value, label]) => (
                <li key={value}>
                  <button
                    type="button"
                    className={filterRowClass(statusFilter === value)}
                    aria-current={statusFilter === value ? 'true' : undefined}
                    onClick={() => setStatusFilter(value)}
                  >
                    <span className="truncate">{label}</span>
                    <span className="badge badge-neutral badge-sm ml-auto shrink-0">
                      {countsByStatus[value] ?? 0}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <form
            className="flex-shrink-0 border-t border-gray-200 p-3 flex items-center gap-2"
            onSubmit={handleCreateCategory}
          >
            <label className="sr-only" htmlFor="eca-cat-new">Nouvelle catégorie</label>
            <input
              id="eca-cat-new"
              className="input flex-1 min-w-0 text-sm"
              placeholder="Nouvelle catégorie"
              value={newCatName}
              onChange={e => setNewCatName(e.target.value)}
            />
            <button
              type="submit"
              className="btn btn-primary btn-sm shrink-0"
              disabled={!newCatName.trim() || catBusy}
              aria-label="Ajouter la catégorie"
            >
              <Plus size={16} aria-hidden="true" />
            </button>
          </form>
        </aside>

        <main className="min-h-0 flex-1 overflow-y-auto p-8">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">ECA — Modèles de workflow</h1>
                <p className="text-sm text-slate-500 mt-1">
                  Sélectionnez un modèle pour l'éditer visuellement.
                </p>
              </div>
              <button
                onClick={() => navigate('/eca-editor/new')}
                className="btn btn-primary flex items-center gap-2"
              >
                <span className="text-base leading-none">+</span>
                Nouveau modèle
              </button>
            </div>

            {/* Recherche — second axe du filtre, cumulatif avec la catégorie */}
            <div className="relative mb-6">
              <label className="sr-only" htmlFor="eca-search">
                Rechercher un modèle par intitulé ou identifiant
              </label>
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                <Search size={16} aria-hidden="true" />
              </span>
              <input
                id="eca-search"
                type="search"
                className="input pl-9"
                placeholder="Rechercher un modèle…"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </div>

            {modelError && (
              <div className="alert alert-danger mb-4" role="alert">
                {modelError}
              </div>
            )}

            {/* States */}
            {loading && (
              <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Chargement des modèles…
              </div>
            )}

            {error && (
              <div className="alert alert-danger">
                <strong>Erreur :</strong> {error}
              </div>
            )}

            {!loading && !error && models.length === 0 && (
              <div className="text-center py-16 text-slate-400 text-sm">
                Aucun modèle ECA trouvé.
              </div>
            )}

            {!loading && !error && models.length > 0 && visibleModels.length === 0 && (
              <div className="empty-state">
                <p className="font-semibold text-slate-700">Aucun modèle ne correspond</p>
                <p className="text-sm text-slate-500">
                  Changez de catégorie ou effacez la recherche pour revoir toute la liste.
                </p>
              </div>
            )}

            {/* Model list */}
            {!loading && !error && visibleModels.length > 0 && (
              <ul className="space-y-3">
                {visibleModels.map(m => {
                  const label    = getLabel(m);
                  const evtCount = Object.keys(m.events     ?? {}).length;
                  const actCount = Object.keys(m.actions    ?? {}).length;
                  const cndCount = Object.keys(m.conditions ?? {}).length;
                  const enabled  = isEnabled(m);

                  return (
                    <li
                      key={m.id}
                      className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-iris-300 hover:shadow-sm transition-all group"
                    >
                      <button
                        onClick={() => navigate(`/eca-editor/${m.id}`)}
                        className="w-full text-left px-5 pt-4 pb-3"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <span className="font-semibold text-slate-800 group-hover:text-iris-700 transition-colors break-words">
                              {label}
                            </span>
                            <div className="text-xs text-slate-400 mt-1 font-mono">{m.id}</div>
                          </div>

                          <div className="shrink-0 flex items-center gap-3 text-xs text-slate-500">
                            {evtCount > 0 && (
                              <span className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-iris-500 inline-block" />
                                {evtCount} event{evtCount > 1 ? 's' : ''}
                              </span>
                            )}
                            {actCount > 0 && (
                              <span className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-sky-500 inline-block" />
                                {actCount} action{actCount > 1 ? 's' : ''}
                              </span>
                            )}
                            {cndCount > 0 && (
                              <span className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
                                {cndCount} condition{cndCount > 1 ? 's' : ''}
                              </span>
                            )}
                            <svg className="w-4 h-4 text-slate-300 group-hover:text-iris-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </button>

                      {/* Affectation — hors du bouton d'ouverture : deux
                          commandes ne peuvent pas s'imbriquer. */}
                      <div className="flex items-center gap-2 border-t border-slate-100 bg-slate-50 px-5 py-2">
                        <span
                          className="field-label text-xs shrink-0"
                          id={`eca-cat-of-${domIdOf(m)}`}
                        >
                          Catégories
                        </span>
                        <CategoryTagsField
                          modelId={m.id}
                          labelledBy={`eca-cat-of-${domIdOf(m)}`}
                          tags={categoriesOf(m).map(id => ({
                            value: id,
                            label: catOptions.find(o => o.value === id)?.label ?? id,
                          }))}
                          options={catOptions}
                          onAdd={name => handleAddCategory(m, name)}
                          onRemove={id => handleRemoveCategory(m, id)}
                        />

                        {/* Activation — `status` du config entity : un modèle
                            désactivé reste éditable mais ne s'exécute plus. */}
                        <div className="ml-auto flex shrink-0 items-center gap-2">
                          <span className={`text-xs ${enabled ? 'text-emerald-700' : 'text-slate-500'}`}>
                            {enabled ? 'Actif' : 'Désactivé'}
                          </span>
                          <Toggle
                            checked={enabled}
                            onChange={() => handleToggleStatus(m)}
                            label={`${enabled ? 'Désactiver' : 'Activer'} le modèle ${label}`}
                          />
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </main>
      </div>

      <ConfirmTrashModal
        isOpen={!!catToDelete}
        variant="purge"
        title="Supprimer cette catégorie ?"
        description={
          `« ${catToDelete?.data?.name ?? ''} » sera supprimée définitivement.`
          + (deletedCatCount > 0
            ? ` ${deletedCatCount} modèle${deletedCatCount > 1 ? 's' : ''} perd${deletedCatCount > 1 ? 'ent' : ''} cette catégorie ; les autres catégories et les modèles eux-mêmes sont conservés.`
            : '')
        }
        confirmLabel="Supprimer"
        loading={catBusy}
        onConfirm={handleDeleteCategory}
        onCancel={() => setCatToDelete(null)}
      />
    </div>
  );
};
